import { useState, useEffect } from 'react';
import { ButtonStyleSelector, ImagePreview, MediaPickerModal } from '../../components/carousel';
import carouselConfig from '../../config/carousel.config';
import './HeroSlideEditModal.css';

/**
 * HeroSlideEditModal Component
 * Modal dialog for editing hero slide content
 *
 * Props:
 * - slide: HeroSlide object to edit
 * - isOpen: boolean - Whether modal is open
 * - onClose: () => void - Close handler
 * - onSave: (id, data) => Promise - Save handler
 * - isSaving: boolean - Saving state
 */
function HeroSlideEditModal({ slide, isOpen, onClose, onSave, isSaving }) {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    buttonStyle: '',
    readMoreContent: '',
    imageUrl: '',
    isActive: true,
  });
  const [errors, setErrors] = useState({});
  const [isDirty, setIsDirty] = useState(false);
  const [isMediaPickerOpen, setIsMediaPickerOpen] = useState(false);

  // Initialize form data when slide changes
  useEffect(() => {
    if (slide) {
      setFormData({
        title: slide.title || '',
        subtitle: slide.subtitle || '',
        buttonText: slide.buttonText || '',
        buttonLink: slide.buttonLink || '',
        buttonStyle: slide.buttonStyle || '',
        readMoreContent: slide.readMoreContent || '',
        imageUrl: slide.imageUrl || '',
        isActive: slide.isActive ?? true,
      });
      setErrors({});
      setIsDirty(false);
    }
  }, [slide]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen && !isSaving) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, isSaving, onClose]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setIsDirty(true);
    // Clear error when field is edited
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handle button style change
  const handleButtonStyleChange = (value) => {
    setFormData((prev) => ({ ...prev, buttonStyle: value }));
    setIsDirty(true);
  };

  // Handle image selection from media picker
  const handleImageSelect = (url) => {
    setFormData((prev) => ({ ...prev, imageUrl: url }));
    setIsDirty(true);
    if (errors.imageUrl) {
      setErrors((prev) => ({ ...prev, imageUrl: null }));
    }
  };

  // Validate form
  const validate = () => {
    const newErrors = {};
    const { validation } = carouselConfig;

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.length > validation.title.maxLength) {
      newErrors.title = `Title must be at most ${validation.title.maxLength} characters`;
    }

    if (!formData.imageUrl.trim()) {
      newErrors.imageUrl = 'Image URL is required';
    } else {
      try {
        new URL(formData.imageUrl);
      } catch {
        newErrors.imageUrl = 'Please enter a valid URL';
      }
    }

    if (formData.subtitle && formData.subtitle.length > validation.subtitle.maxLength) {
      newErrors.subtitle = `Subtitle must be at most ${validation.subtitle.maxLength} characters`;
    }

    if (formData.buttonText && formData.buttonText.length > validation.buttonText.maxLength) {
      newErrors.buttonText = `Button text must be at most ${validation.buttonText.maxLength} characters`;
    }

    if (formData.buttonLink && formData.buttonLink.length > validation.buttonLink.maxLength) {
      newErrors.buttonLink = `Button link must be at most ${validation.buttonLink.maxLength} characters`;
    }

    if (formData.readMoreContent && formData.readMoreContent.length > validation.readMoreContent.maxLength) {
      newErrors.readMoreContent = `Read more content must be at most ${validation.readMoreContent.maxLength} characters`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    try {
      await onSave(slide.id, {
        title: formData.title.trim(),
        subtitle: formData.subtitle.trim() || null,
        buttonText: formData.buttonText.trim() || null,
        buttonLink: formData.buttonLink.trim() || null,
        buttonStyle: formData.buttonStyle || null,
        readMoreContent: formData.readMoreContent.trim() || null,
        imageUrl: formData.imageUrl.trim(),
        isActive: formData.isActive,
      });
    } catch (err) {
      // Error handled by parent
    }
  };

  // Handle backdrop click
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget && !isSaving) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={handleBackdropClick}>
      <div className="modal hero-slide-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>Edit Slide {slide?.displayOrder}</h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            disabled={isSaving}
            aria-label="Close modal"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              {/* Left Column - Form Fields */}
              <div className="form-column">
                {/* Title */}
                <div className="form-group">
                  <label className="form-label" htmlFor="title">
                    Title <span className="required">*</span>
                  </label>
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className={`form-input ${errors.title ? 'is-invalid' : ''}`}
                    placeholder="Enter slide title"
                    value={formData.title}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  {errors.title && <div className="invalid-feedback">{errors.title}</div>}
                </div>

                {/* Subtitle */}
                <div className="form-group">
                  <label className="form-label" htmlFor="subtitle">
                    Subtitle
                  </label>
                  <textarea
                    id="subtitle"
                    name="subtitle"
                    className={`form-input ${errors.subtitle ? 'is-invalid' : ''}`}
                    placeholder="Enter slide subtitle"
                    rows={3}
                    value={formData.subtitle}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  {errors.subtitle && <div className="invalid-feedback">{errors.subtitle}</div>}
                </div>

                {/* Button Text */}
                <div className="form-group">
                  <label className="form-label" htmlFor="buttonText">
                    Button Text
                  </label>
                  <input
                    id="buttonText"
                    name="buttonText"
                    type="text"
                    className={`form-input ${errors.buttonText ? 'is-invalid' : ''}`}
                    placeholder="SHOP NOW"
                    value={formData.buttonText}
                    onChange={handleChange}
                    disabled={isSaving}
                  />
                  {errors.buttonText && <div className="invalid-feedback">{errors.buttonText}</div>}
                </div>

                {/* Button Style */}
                <ButtonStyleSelector
                  value={formData.buttonStyle}
                  onChange={handleButtonStyleChange}
                  error={errors.buttonStyle}
                  disabled={isSaving}
                />

                {/* Read More Content */}
                <div className="form-group">
                  <label className="form-label" htmlFor="readMoreContent">
                    Read More Content
                  </label>
                  <textarea
                    id="readMoreContent"
                    name="readMoreContent"
                    className={`form-input ${errors.readMoreContent ? 'is-invalid' : ''}`}
                    placeholder="A short text snippet shown when button is clicked..."
                    rows={3}
                    value={formData.readMoreContent}
                    onChange={handleChange}
                    disabled={isSaving}
                    maxLength={carouselConfig.validation.readMoreContent.maxLength}
                  />
                  <div className="form-help-row">
                    <span className="form-help">
                      When set, clicking the button shows this content in a modal.
                    </span>
                    <span className={`char-count ${formData.readMoreContent.length > carouselConfig.validation.readMoreContent.maxLength * 0.9 ? 'char-count--warning' : ''}`}>
                      {formData.readMoreContent.length}/{carouselConfig.validation.readMoreContent.maxLength}
                    </span>
                  </div>
                  {errors.readMoreContent && <div className="invalid-feedback">{errors.readMoreContent}</div>}
                </div>

                {/* Active Toggle */}
                <div className="form-group form-group--checkbox">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="isActive"
                      checked={formData.isActive}
                      onChange={handleChange}
                      disabled={isSaving}
                    />
                    <span>Active (visible on website)</span>
                  </label>
                </div>
              </div>

              {/* Right Column - Preview */}
              <div className="preview-column">
                <label className="form-label">Slide Image</label>
                <div className="image-preview-wrapper">
                  <ImagePreview
                    url={formData.imageUrl}
                    alt="Slide preview"
                    aspectRatio="4:3"
                  />
                  <button
                    type="button"
                    className="change-image-btn"
                    onClick={() => setIsMediaPickerOpen(true)}
                    disabled={isSaving}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909m-18 3.75h16.5a1.5 1.5 0 001.5-1.5V6a1.5 1.5 0 00-1.5-1.5H3.75A1.5 1.5 0 002.25 6v12a1.5 1.5 0 001.5 1.5zm10.5-11.25h.008v.008h-.008V8.25zm.375 0a.375.375 0 11-.75 0 .375.375 0 01.75 0z"
                      />
                    </svg>
                    Change Image
                  </button>
                  {errors.imageUrl && (
                    <div className="image-error">{errors.imageUrl}</div>
                  )}
                  <div className="recommended-size-badge">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      strokeWidth={1.5}
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                      />
                    </svg>
                    <span>
                      Recommended size:{' '}
                      <strong>
                        {carouselConfig.recommendedImageSize.width} x {carouselConfig.recommendedImageSize.height} pixels
                      </strong>
                    </span>
                  </div>
                </div>
                <p className="preview-hint">
                  Click "Change Image" to select from Media Library
                </p>
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onClose}
              disabled={isSaving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={isSaving || !isDirty}
            >
              {isSaving ? (
                <>
                  <span className="btn-spinner" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Media Picker Modal */}
      <MediaPickerModal
        isOpen={isMediaPickerOpen}
        onClose={() => setIsMediaPickerOpen(false)}
        onSelect={handleImageSelect}
        currentUrl={formData.imageUrl}
      />
    </div>
  );
}

export default HeroSlideEditModal;
